import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { api, ApiError } from "./api";
import { useTranslation } from "./i18n";
import { Button } from "./components/ui/button";
import { ArrowUpRight, LockKeyhole, LoaderCircle } from "lucide-react";
import { Brand } from "./components/ui/brand";

type LoginChoice = { provider: "google" | "wechat" | "feishu"; enterpriseId?: string };
type LoginAttempt = LoginChoice & { attemptId: string; authUrl: string; expiresIn: number; expiresAt: number };

export function LoginPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const enterprises = useQuery({
    queryKey: ["auth-feishu-providers"],
    queryFn: ({ signal }) => api<{ id: string; name: string }[]>("api/auth/feishu/providers", { signal }),
    retry: false, staleTime: Infinity,
  });
  const popup = useRef<Window | null>(null);
  const [popupBlocked, setPopupBlocked] = useState(false);
  const start = useMutation({
    gcTime: 0,
    mutationFn: async (choice: LoginChoice) => {
      const attempt = await api<Omit<LoginAttempt, "expiresAt" | keyof LoginChoice>>(`api/auth/${choice.provider}/start`, {
        method: "POST", body: JSON.stringify(choice.provider === "feishu" ? { providerId: choice.enterpriseId } : {}),
      });
      return { ...attempt, ...choice, expiresAt: Date.now() + attempt.expiresIn * 1000 };
    },
    onSuccess: (attempt) => {
      if (popup.current && !popup.current.closed) popup.current.location.href = attempt.authUrl;
    },
    onError: () => popup.current?.close(),
  });
  const attempt = start.data;
  const wait = useQuery<{ status: "pending" | "authenticated" }>({
    queryKey: ["social-login", attempt?.provider, attempt?.enterpriseId, attempt?.attemptId],
    enabled: Boolean(attempt),
    retry: false,
    gcTime: 0,
    staleTime: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchIntervalInBackground: true,
    refetchInterval: (query) => query.state.error || query.state.data?.status === "authenticated" ? false : 2000,
    queryFn: async ({ signal }) => {
      if (!attempt || Date.now() >= attempt.expiresAt) throw new ApiError("Login expired", 401, "LOGIN_ATTEMPT_EXPIRED");
      return api<{ status: "pending" | "authenticated" }>(`api/auth/${attempt.provider}/wait`, {
        method: "POST", body: JSON.stringify({ attemptId: attempt.attemptId }), signal,
      });
    },
  });
  useEffect(() => () => popup.current?.close(), []);
  useEffect(() => {
    if (wait.data?.status === "authenticated") {
      popup.current?.close();
      // A full navigation discards all cached private data from the previous session.
      window.location.assign("/");
    }
  }, [wait.data?.status]);

  const error = start.error ?? wait.error;
  const waiting = start.isPending || Boolean(attempt && !wait.error);
  function signIn(choice: LoginChoice) {
    if (waiting) return;
    // Open synchronously in the click gesture so browsers do not block authorization.
    const opened = window.open("about:blank", "_blank");
    if (!opened) {
      setPopupBlocked(true);
      return;
    }
    setPopupBlocked(false);
    opened.opener = null;
    popup.current?.close();
    popup.current = opened;
    start.reset();
    start.mutate(choice);
  }
  async function cancelFeishu() {
    if (!attempt || attempt.provider !== "feishu") return;
    const saved = attempt;
    await queryClient.cancelQueries({ queryKey: ["social-login", saved.provider, saved.enterpriseId, saved.attemptId] });
    try { popup.current?.close(); } catch { /* Cross-origin closing is best effort. */ }
    popup.current = null;
    start.reset();
    void api("api/auth/feishu/cancel", { method: "POST", body: JSON.stringify({ attemptId: saved.attemptId }) }).catch(() => {});
  }
  const selected = attempt ?? start.variables;
  const errorKey = error instanceof ApiError && error.code === "FEISHU_EMPLOYEE_REQUIRED"
    ? "auth.feishuEmployeeRequired" : error instanceof ApiError && error.code?.startsWith("FEISHU_")
      ? "auth.feishuUnavailable" : error instanceof ApiError && error.code === "THIRDPARTY_LOGIN_NOT_CONFIGURED"
    ? "auth.socialNotConfigured" : error instanceof ApiError && error.code === "LOGIN_ATTEMPT_EXPIRED"
      ? "auth.socialExpired" : "auth.socialFailed";
  return (
    <div className="flex min-h-[calc(100dvh-58px)] items-center justify-center bg-muted/20 px-4 py-10 md:px-8 md:py-16">
      <div className="grid w-full max-w-[900px] overflow-hidden rounded-2xl border border-border bg-background shadow-sm md:grid-cols-2">
        <aside className="hidden flex-col justify-between border-r border-border bg-muted/40 p-10 md:flex">
          <Link className="inline-flex self-start" to="/" aria-label={t("nav.homeAria")}><Brand /></Link>
          <div className="py-16">
            <div className="mb-8 flex size-16 items-center justify-center rounded-2xl border border-border bg-background">
              <img src="/nexra-mark.svg" className="size-10" alt="" />
            </div>
            <h2 className="max-w-64 whitespace-pre-line text-3xl font-semibold leading-snug tracking-tight">{t("auth.socialHeadline")}</h2>
            <p className="mt-4 max-w-64 text-sm leading-6 text-muted-foreground">{t("auth.socialProductDescription")}</p>
          </div>
          <p className="text-xs text-muted-foreground">Nexra · Model Gateway</p>
        </aside>
        <section className="flex min-h-[480px] flex-col justify-center px-7 py-10 sm:px-10 md:py-14">
          <Link className="mb-10 inline-flex self-start md:hidden" to="/" aria-label={t("nav.homeAria")}><Brand /></Link>
          <div className="mb-8">
            <h1 className="text-2xl font-semibold tracking-tight">{t("auth.socialTitle")}</h1>
            <p className="mt-3 text-sm leading-6 text-muted-foreground">{t("auth.socialDescription")}</p>
          </div>
          <div className="grid gap-3">
            <Button variant="outline" size="lg" className="w-full justify-between" disabled={waiting} onClick={() => signIn({ provider: "google" })}>
              <span className="inline-flex items-center gap-3"><GoogleIcon />{t("auth.googleSignIn")}</span><ArrowUpRight className="size-4 text-muted-foreground" />
            </Button>
            <Button variant="outline" size="lg" className="w-full justify-between" disabled={waiting} onClick={() => signIn({ provider: "wechat" })}>
              <span className="inline-flex items-center gap-3"><WechatIcon />{t("auth.wechatSignIn")}</span><ArrowUpRight className="size-4 text-muted-foreground" />
            </Button>
            {enterprises.data?.map(enterprise => (
              <Button key={enterprise.id} variant="outline" size="lg" className="w-full justify-between" disabled={waiting}
                onClick={() => signIn({ provider: "feishu", enterpriseId: enterprise.id })}>
                <span className="inline-flex items-center gap-3"><FeishuIcon />{t("auth.feishuSignIn", { enterprise: enterprise.name })}</span><ArrowUpRight className="size-4 text-muted-foreground" />
              </Button>
            ))}
          </div>
          <div className="mt-5 min-h-20">
            {enterprises.error ? <p role="alert" className="text-sm leading-6 text-danger">{t("auth.feishuUnavailable")}</p> : null}
            {error ? <p role="alert" className="text-sm leading-6 text-danger">{t(errorKey)}</p> : null}
            {popupBlocked ? <p role="alert" className="text-sm leading-6 text-danger">{t("auth.socialPopupBlocked")}</p> : null}
            {waiting ? <p role="status" className="flex items-start gap-2 text-sm leading-6 text-muted-foreground"><LoaderCircle className="mt-1 size-4 shrink-0 animate-spin" />{t(selected?.provider === "feishu" ? "auth.feishuWaiting" : selected?.provider === "wechat" ? "auth.wechatWaiting" : "auth.googleWaiting")}</p> : null}
            {waiting && attempt?.provider === "feishu" ? <Button variant="link" size="sm" onClick={() => void cancelFeishu()}>{t("auth.cancelFeishu")}</Button> : null}
          </div>
          <p className="mt-5 text-xs leading-5 text-muted-foreground">{t("auth.socialCreateAccount")}</p>
          <div className="mt-7 flex items-center gap-2 border-t border-border pt-5 text-xs text-muted-foreground"><LockKeyhole className="size-3.5" />{t("auth.socialSecurity")}</div>
        </section>
      </div>
    </div>
  );
}

function GoogleIcon() {
  return <svg className="size-5" viewBox="0 0 48 48" aria-hidden="true"><path fill="#4285F4" d="M43.6 24.5c0-1.5-.1-3-.4-4.5H24v8.5h11a9.4 9.4 0 0 1-4.1 6.2v5.2h6.7c3.9-3.6 6-8.9 6-15.4Z" /><path fill="#34A853" d="M24 44c5.4 0 10-1.8 13.3-4.9l-6.7-5.2c-1.8 1.2-4 1.9-6.6 1.9-5.2 0-9.6-3.5-11.2-8.2H5.9v5.4A20 20 0 0 0 24 44Z" /><path fill="#FBBC05" d="M12.8 27.6a12 12 0 0 1 0-7.2V15H5.9a20 20 0 0 0 0 18l6.9-5.4Z" /><path fill="#EA4335" d="M24 12.2c2.9 0 5.5 1 7.5 2.9l5.6-5.6A19 19 0 0 0 24 4 20 20 0 0 0 5.9 15l6.9 5.4c1.6-4.7 6-8.2 11.2-8.2Z" /></svg>;
}
function FeishuIcon() {
  // Reuse the approved Feishu brand geometry from PaperConsole / StarCut.
  return <svg className="size-5 shrink-0" viewBox="144 176 736 736" aria-hidden="true"><path d="M524.8 550.4c38.4-34.133333 81.066667-85.333333 149.333333-110.933333h4.266667c-17.066667-85.333333-85.333333-162.133333-85.333333-162.133334H268.8s106.666667 64 196.266667 183.466667c25.6 34.133333 46.933333 64 59.733333 89.6z" fill="#00D6B9" /><path d="M669.866667 439.466667c-85.333333 34.133333-136.533333 110.933333-179.2 140.8-29.866667 21.333333-46.933333 29.866667-64 42.666666 55.466667 25.6 132.266667 42.666667 192 46.933334s98.133333-21.333333 123.733333-51.2c21.333333-29.866667 98.133333-166.4 98.133333-166.4s-85.333333-46.933333-170.666666-12.8z" fill="#133C9A" /><path d="M742.4 618.666667c-25.6 29.866667-59.733333 46.933333-119.466667 42.666666-238.933333-12.8-413.866667-200.533333-448-230.4-4.266667 4.266667 0 273.066667 0 277.333334 21.333333 51.2 136.533333 85.333333 204.8 89.6 217.6 17.066667 362.666667-179.2 362.666667-179.2z" fill="#3370FF" /></svg>;
}
function WechatIcon() {
  return <svg className="size-5" viewBox="0 0 24 24" aria-hidden="true"><path fill="#07C160" d="M9.2 3C4.7 3 1 5.9 1 9.5c0 2.1 1.3 4 3.4 5.2l-.9 2.8 3-1.5c.9.2 1.8.3 2.7.3h.8a6.7 6.7 0 0 1-.6-2.8c0-3.7 3.4-6.7 7.7-6.7h.2C16 4.6 12.9 3 9.2 3Z" /><path fill="#07C160" d="M23 13.5c0-3-2.8-5.5-6.3-5.5s-6.3 2.5-6.3 5.5 2.8 5.5 6.3 5.5c.7 0 1.4-.1 2.1-.3l2.4 1.3-.7-2.3a5.3 5.3 0 0 0 2.5-4.2Z" /><g fill="white"><circle cx="6.4" cy="7.6" r="1" /><circle cx="12" cy="7.6" r="1" /><circle cx="14.4" cy="12.1" r=".8" /><circle cx="18.7" cy="12.1" r=".8" /></g></svg>;
}
